import { StaffPartner, getStaffPartners } from './partnerService';
import { BookingRecord, getStoredBookings } from '../utils/excelExport';

/**
 * The 4 official 2-hour doorstep salon time slots.
 * Starting 10:00 AM - 12:00 PM, 12:00 PM - 2:00 PM, 2:00 PM - 4:00 PM, 4:00 PM - 6:00 PM.
 */
export const TIME_SLOTS = [
  '10:00 AM - 12:00 PM',
  '12:00 PM - 2:00 PM',
  '2:00 PM - 4:00 PM',
  '4:00 PM - 6:00 PM'
];

/**
 * Backward-compatibility alias for TIME_SLOTS.
 */
export const HOURLY_TIME_SLOTS = TIME_SLOTS;

export const LAUNCH_DATE = '2026-10-11'; // Official Blishka launch date

/**
 * Any order with estimated duration exceeding 2:30 hours (150 minutes)
 * automatically blocks the consecutive/next time slot.
 */
export const BLOCK_NEXT_SLOT_THRESHOLD_MINS = 150;

export interface SlotAvailabilityInfo {
  slot: string;
  isAvailable: boolean;
  bookedCount: number;
  totalCapacity: number;
  remaining: number;
  nextSlotBlocked?: boolean;
}

/**
 * Returns true if the order duration is 2 hours 30 mins (150 mins) or more.
 */
export function isLongServiceOrder(durationMinutes?: number): boolean {
  return typeof durationMinutes === 'number' && durationMinutes >= BLOCK_NEXT_SLOT_THRESHOLD_MINS;
}

/**
 * Calculates total estimated duration in minutes from an items list.
 */
export function calculateBookingDuration(items?: any[]): number {
  if (!items || !Array.isArray(items)) return 0;
  return items.reduce((sum, it) => sum + (Number(it?.durationMinutes) || 30), 0);
}

/**
 * Normalizes any legacy or variant slot string to one of the 4 standard slots.
 */
export function normalizeToFourSlots(slot: string): string {
  if (!slot) return TIME_SLOTS[0];
  const trimmed = slot.trim();
  if (TIME_SLOTS.includes(trimmed)) return trimmed;

  const hour = parseSlotHour(trimmed);
  if (hour < 12) return TIME_SLOTS[0]; // 10:00 AM - 12:00 PM
  if (hour < 14) return TIME_SLOTS[1]; // 12:00 PM - 2:00 PM
  if (hour < 16) return TIME_SLOTS[2]; // 2:00 PM - 4:00 PM
  return TIME_SLOTS[3]; // 4:00 PM - 6:00 PM
}

/**
 * Returns the consecutive/next time slot in the schedule, or null if it's the last slot.
 */
export function getNextSlot(slot: string): string | null {
  const norm = normalizeToFourSlots(slot);
  const idx = TIME_SLOTS.indexOf(norm);
  if (idx >= 0 && idx < TIME_SLOTS.length - 1) {
    return TIME_SLOTS[idx + 1];
  }
  return null;
}

/**
 * Parses the 24-hour hour from a slot string (e.g. '10:00 AM - 12:00 PM' -> 10, '2:00 PM - 4:00 PM' -> 14)
 */
export function parseSlotHour(slot: string): number {
  if (!slot) return 10;
  const startPart = slot.includes('-') ? slot.split('-')[0].trim() : slot.trim();
  const [timePart, modifier] = startPart.split(' ');
  let [rawHours] = (timePart || '10:00').split(':').map(Number);
  const mod = (modifier || '').toUpperCase();
  if (mod === 'PM' && rawHours < 12) rawHours += 12;
  if (mod === 'AM' && rawHours === 12) rawHours = 0;
  return rawHours;
}

/**
 * Returns today's date formatted as YYYY-MM-DD in the user's local timezone.
 */
export function getLocalTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Returns tomorrow's date formatted as YYYY-MM-DD in the user's local timezone.
 */
export function getLocalTomorrowDateString(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const day = String(tomorrow.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Validates whether a time slot is eligible based on the advance booking rule:
 * - If date is in the past: false
 * - If date is in the future: true (all 4 slots allowed)
 * - If date is today:
 *   Only allows slots strictly after current hour with at least 30 mins lead time.
 */
export function isSlotAvailableTimeWise(slot: string, dateStr: string): boolean {
  if (!dateStr) return false;
  const todayStr = getLocalTodayDateString();
  if (dateStr < todayStr) return false;
  if (dateStr > todayStr) return true;

  const now = new Date();
  const slotHour = parseSlotHour(slot);
  const currentHour = now.getHours();

  // If slot start hour is current hour or earlier, block it
  if (slotHour <= currentHour) return false;

  // Minimum advance notice buffer for beautician preparation and dispatch
  const [y, m, d] = dateStr.split('-').map(Number);
  const slotDate = new Date(y, m - 1, d, slotHour, 0, 0, 0);
  const diffMinutes = (slotDate.getTime() - now.getTime()) / (1000 * 60);
  return diffMinutes >= 30;
}

/**
 * Returns the list of visible slots for a given date.
 * If the selected date is today, only slots with advance notice are returned.
 * If the selected date is in the future (tomorrow onwards), all 4 slots are returned.
 */
export function getVisibleSlotsForDate(dateStr: string): string[] {
  if (!dateStr) return TIME_SLOTS;
  const todayStr = getLocalTodayDateString();
  if (dateStr === todayStr) {
    return TIME_SLOTS.filter((s) => isSlotAvailableTimeWise(s, dateStr));
  }
  return TIME_SLOTS;
}

/**
 * Checks real-time slot availability for a given date and optional cart duration.
 * - Enforces 4 fixed slots (10-12, 12-2, 2-4, 4-6).
 * - If any booking on this date has duration >= 150 mins (> 2:30 hours), it also blocks the next slot.
 * - If cartDurationMinutes >= 150 mins, slots are marked available only if the consecutive slot also has capacity.
 */
export async function getSlotAvailability(
  date: string,
  cartDurationMinutes?: number
): Promise<Record<string, SlotAvailabilityInfo>> {
  const result: Record<string, SlotAvailabilityInfo> = {};
  const todayStr = getLocalTodayDateString();
  const isToday = date === todayStr;

  // If date is in the past, all slots are unavailable
  if (date && date < todayStr) {
    TIME_SLOTS.forEach((slot) => {
      result[slot] = {
        slot,
        isAvailable: false,
        bookedCount: 0,
        totalCapacity: 3,
        remaining: 0
      };
    });
    return result;
  }

  try {
    const durationQuery = cartDurationMinutes ? `&duration=${cartDurationMinutes}` : '';
    const res = await fetch(`/api/bookings?slotsForDate=${encodeURIComponent(date)}${durationQuery}`, {
      headers: { 'Cache-Control': 'no-store' }
    });

    if (res.ok) {
      const data = (await res.json()) as {
        success: boolean;
        totalCapacity?: number;
        slots?: Record<
          string,
          { available: boolean; bookedCount: number; remaining: number; totalCapacity: number }
        >;
      };

      if (data && data.slots) {
        TIME_SLOTS.forEach((slot) => {
          const s = data.slots?.[slot];
          const isTimeOk = !isToday || isSlotAvailableTimeWise(slot, date);
          if (s) {
            result[slot] = {
              slot,
              isAvailable: Boolean(s.available) && isTimeOk,
              bookedCount: s.bookedCount || 0,
              totalCapacity: s.totalCapacity || data.totalCapacity || 3,
              remaining: isTimeOk ? (s.remaining !== undefined ? s.remaining : (s.available ? 1 : 0)) : 0
            };
          } else {
            result[slot] = {
              slot,
              isAvailable: isTimeOk,
              bookedCount: 0,
              totalCapacity: data.totalCapacity || 3,
              remaining: isTimeOk ? (data.totalCapacity || 3) : 0
            };
          }
        });

        return result;
      }
    }
  } catch (err) {
    console.warn('Could not query slot availability from D1, calculating from local store:', err);
  }

  // ── Local Fallback Calculation ──
  const activeStaff = getStaffPartners().filter(
    (s) => s.isActive !== false && s.isOnline !== false
  );
  const totalCapacity = Math.max(1, activeStaff.length);

  const dayBookings = getStoredBookings().filter(
    (b) => b.preferredDate === date && b.serviceStatus !== 'cancelled'
  );

  // Map each staff member to slots they occupy on this date
  const staffOccupiedSlots = new Map<string, Set<string>>();
  activeStaff.forEach((s) => staffOccupiedSlots.set(s.id, new Set<string>()));

  // Unassigned bookings (without partnerId) occupying each slot
  const unassignedOccupiedCounts: Record<string, number> = {
    [TIME_SLOTS[0]]: 0,
    [TIME_SLOTS[1]]: 0,
    [TIME_SLOTS[2]]: 0,
    [TIME_SLOTS[3]]: 0
  };

  dayBookings.forEach((b) => {
    const normSlot = normalizeToFourSlots(b.preferredSlot);
    const duration = calculateBookingDuration(b.items);
    const blocksNext = isLongServiceOrder(duration);
    const nextSlot = blocksNext ? getNextSlot(normSlot) : null;

    if (b.partnerId && staffOccupiedSlots.has(b.partnerId)) {
      staffOccupiedSlots.get(b.partnerId)!.add(normSlot);
      if (nextSlot) {
        staffOccupiedSlots.get(b.partnerId)!.add(nextSlot);
      }
    } else {
      unassignedOccupiedCounts[normSlot] = (unassignedOccupiedCounts[normSlot] || 0) + 1;
      if (nextSlot) {
        unassignedOccupiedCounts[nextSlot] = (unassignedOccupiedCounts[nextSlot] || 0) + 1;
      }
    }
  });

  TIME_SLOTS.forEach((slot) => {
    const freeStaffInSlot = activeStaff.filter(
      (s) => !staffOccupiedSlots.get(s.id)?.has(slot)
    );
    const unassigned = unassignedOccupiedCounts[slot] || 0;
    const remaining = Math.max(0, freeStaffInSlot.length - unassigned);
    const isTimeOk = !isToday || isSlotAvailableTimeWise(slot, date);

    // If current cart needs next slot blocked too:
    let hasConsecutiveAvailability = true;
    if (cartDurationMinutes && isLongServiceOrder(cartDurationMinutes)) {
      const next = getNextSlot(slot);
      if (next) {
        const freeStaffInBoth = activeStaff.filter(
          (s) => !staffOccupiedSlots.get(s.id)?.has(slot) && !staffOccupiedSlots.get(s.id)?.has(next)
        );
        const nextUnassigned = unassignedOccupiedCounts[next] || 0;
        const nextRemaining = Math.max(0, freeStaffInBoth.length - Math.max(unassigned, nextUnassigned));
        if (nextRemaining <= 0) {
          hasConsecutiveAvailability = false;
        }
      }
    }

    const isAvailable = remaining > 0 && isTimeOk && hasConsecutiveAvailability;

    result[slot] = {
      slot,
      isAvailable,
      bookedCount: totalCapacity - remaining,
      totalCapacity,
      remaining: isTimeOk ? remaining : 0,
      nextSlotBlocked: cartDurationMinutes ? isLongServiceOrder(cartDurationMinutes) : false
    };
  });

  return result;
}

/**
 * Assigns an available beautician to a slot using round-robin fair dispatch.
 * 
 * Rules:
 * 1. Beautician must be active and online (on duty).
 * 2. Beautician must NOT already have a non-cancelled booking in this slot (or spillover from previous slot).
 * 3. If order duration is >= 150 mins (> 2:30 hours), beautician must ALSO be free in the next slot.
 * 4. Among available candidates, the one with fewest bookings today is picked (fair load balancing).
 * 5. Returns null if all beauticians are booked for this slot (slot is full).
 */
export function selectRoundRobinBeautician(
  date: string,
  slot: string,
  orderDurationMinutes?: number,
  providedStaff?: StaffPartner[],
  providedBookings?: BookingRecord[]
): {
  beautician: StaffPartner;
  remainingCandidates: number;
} | null {
  if (date && date < getLocalTodayDateString()) {
    return null;
  }

  const staffPool = (providedStaff || getStaffPartners()).filter(
    (s) => s.isActive !== false && s.isOnline !== false
  );

  if (staffPool.length === 0) {
    const fallback = (providedStaff || getStaffPartners())[0];
    return fallback ? { beautician: fallback, remainingCandidates: 0 } : null;
  }

  const allBookings = providedBookings || getStoredBookings();
  const dateBookings = allBookings.filter(
    (b) => b.preferredDate === date && b.serviceStatus !== 'cancelled'
  );

  const normSlot = normalizeToFourSlots(slot);
  const blocksNext = isLongServiceOrder(orderDurationMinutes);
  const nextSlot = blocksNext ? getNextSlot(normSlot) : null;

  // Build map of occupied slots per staff partner
  const staffOccupied = new Map<string, Set<string>>();
  staffPool.forEach((s) => staffOccupied.set(s.id, new Set<string>()));

  dateBookings.forEach((b) => {
    if (!b.partnerId) return;
    const bSlot = normalizeToFourSlots(b.preferredSlot);
    const bDuration = calculateBookingDuration(b.items);
    const bBlocksNext = isLongServiceOrder(bDuration);
    const bNext = bBlocksNext ? getNextSlot(bSlot) : null;

    if (staffOccupied.has(b.partnerId)) {
      staffOccupied.get(b.partnerId)!.add(bSlot);
      if (bNext) {
        staffOccupied.get(b.partnerId)!.add(bNext);
      }
    }
  });

  // Candidate beauticians who are free during normSlot (and nextSlot if long service)
  const availableCandidates = staffPool.filter((s) => {
    const busySlots = staffOccupied.get(s.id);
    if (!busySlots) return true;
    if (busySlots.has(normSlot)) return false;
    if (nextSlot && busySlots.has(nextSlot)) return false;
    return true;
  });

  if (availableCandidates.length === 0) {
    return null;
  }

  // Count how many bookings each candidate has already received on this date (fair load balancing)
  const candidatesWithWorkload = availableCandidates.map((staff) => {
    const jobsOnDate = dateBookings.filter((b) => b.partnerId === staff.id).length;
    return {
      staff,
      jobsOnDate,
      totalJobs: staff.totalJobs || 0
    };
  });

  // Sort by:
  // 1. Least jobs on this date (fair round-robin)
  // 2. Least overall jobs
  // 3. Stable ID
  candidatesWithWorkload.sort((a, b) => {
    if (a.jobsOnDate !== b.jobsOnDate) {
      return a.jobsOnDate - b.jobsOnDate;
    }
    if (a.totalJobs !== b.totalJobs) {
      return a.totalJobs - b.totalJobs;
    }
    return a.staff.id.localeCompare(b.staff.id);
  });

  const selected = candidatesWithWorkload[0].staff;
  return {
    beautician: selected,
    remainingCandidates: availableCandidates.length - 1
  };
}
